/**
 * Camada visível dos avisos da plataforma — tudo flutuando por cima da tela.
 *
 * Banner fica fixo no topo ou no rodapé; aviso de canto e cartão vão para o
 * canto escolhido na campanha; modal estoura no centro com fundo escuro.
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  AvisoBanner,
  AvisoModal,
  AvisoPesquisa,
  AvisoPost,
  AvisoToast,
} from "./AvisoRenderers";
import { useAvisos } from "./CommunicationsProvider";

const CANTOS = {
  "superior-esquerdo": "top-5 left-5 items-start",
  "superior-direito": "top-5 right-5 items-end",
  "inferior-esquerdo": "bottom-5 left-5 items-start",
  "inferior-direito": "bottom-5 right-5 items-end",
};

const canto = (aviso) => CANTOS[aviso.exibicao?.posicao] || CANTOS["inferior-direito"];

/** Só o modal trava a tela: fundo escuro, rolagem presa e cliques bloqueados. */
function ModalBloqueante({ children }) {
  useEffect(() => {
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = antes;
    };
  }, []);

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[1800] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[2px]"
      onWheel={(e) => e.preventDefault()}
      data-testid="communication-modal-overlay"
    >
      {children}
    </div>,
    document.body,
  );
}

const noCentro = (a) =>
  a.formato === "modal" || (a.formato === "pesquisa" && a.pesquisa?.apresentacao === "modal");

export default function CommunicationOverlays() {
  const { avisos, dispensar, clicar, responder, marcarVideo } = useAvisos();
  const central = avisos.find(noCentro);
  const banners = avisos.filter((a) => a.formato === "banner");
  const cantos = avisos.filter(
    (a) => a.formato === "post" || a.formato === "toast" || (a.formato === "pesquisa" && !noCentro(a)),
  );

  const porCanto = {};
  cantos.forEach((a) => {
    const k = CANTOS[a.exibicao?.posicao] ? a.exibicao.posicao : "inferior-direito";
    (porCanto[k] = porCanto[k] || []).push(a);
  });

  return (
    <>
      {banners.map((a) => (
        <div
          key={a.id}
          className={`pointer-events-none fixed inset-x-0 z-[1740] mx-auto w-full max-w-[840px] px-5 ${
            a.exibicao?.posicao === "rodape" ? "bottom-5" : "top-5"
          }`}
          data-testid="communication-banners"
        >
          <div className="pointer-events-auto">
            <AvisoBanner campanha={a} onClose={() => dispensar(a)} onClick={() => clicar(a)} />
          </div>
        </div>
      ))}

      {Object.entries(porCanto).map(([pos, lista]) => (
        <div
          key={pos}
          className={`fixed z-[1750] flex flex-col gap-3 ${canto(lista[0])}`}
          data-testid={`communication-canto-${pos}`}
        >
          {lista.slice(0, 3).map((a) =>
            a.formato === "pesquisa" ? (
              <AvisoPesquisa
                key={a.id}
                campanha={a}
                onClose={() => dispensar(a)}
                onResponder={(nota, comentario) => responder(a, nota, comentario)}
              />
            ) : a.formato === "post" ? (
              <AvisoPost
                key={a.id}
                campanha={a}
                onClose={() => dispensar(a)}
                onClick={() => clicar(a)}
                onVideoEvento={(p) => marcarVideo(a, p)}
              />
            ) : (
              <AvisoToast
                key={a.id}
                campanha={a}
                onClose={() => dispensar(a)}
                onClick={() => clicar(a)}
              />
            ),
          )}
        </div>
      ))}

      {central && (
        <ModalBloqueante>
          {central.formato === "pesquisa" ? (
            <AvisoPesquisa
              campanha={central}
              onClose={() => dispensar(central)}
              onResponder={(nota, comentario) => responder(central, nota, comentario)}
            />
          ) : (
            <AvisoModal
              campanha={central}
              onClose={() => dispensar(central)}
              onClick={() => clicar(central)}
              onVideoEvento={(p) => marcarVideo(central, p)}
            />
          )}
        </ModalBloqueante>
      )}
    </>
  );
}
